import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  X,
  Mic,
  MicOff,
  Loader2,
  Search,
  UserPlus,
  Calendar,
  MapPin,
  FileText,
  Pencil,
} from 'lucide-react'
import { PlaceAutocomplete } from '@/components/PlaceAutocomplete'
import { cn } from '@/lib/utils'
import { useContacts } from '@/hooks/use-contacts'
import { useCreateInteraction } from '@/hooks/use-interactions'
import { useVoiceInput } from '@/hooks/use-voice-input'
import type { Contact, InteractionType, InteractionQuality } from '@/lib/types'

interface QuickLogPopupProps {
  contactId: string
  template: {
    id: string
    name: string
    type: InteractionType
    quality: InteractionQuality | null
    icon: string | null
  }
  onClose: () => void
  onSaved: () => void
}

function formatDateTimeLocal(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function getInitials(firstName: string, lastName: string | null): string {
  return firstName.charAt(0).toUpperCase() + (lastName?.charAt(0).toUpperCase() ?? '')
}

function getContactName(c: { firstName: string; lastName: string | null }): string {
  return [c.firstName, c.lastName].filter(Boolean).join(' ')
}

export function QuickLogPopup({ contactId, template, onClose, onSaved }: QuickLogPopupProps) {
  const { t } = useTranslation()
  const createInteraction = useCreateInteraction()
  const voice = useVoiceInput()

  // Form state
  const [title, setTitle] = useState(template.name)
  const [occurredAt, setOccurredAt] = useState(formatDateTimeLocal(new Date()))
  const [location, setLocation] = useState('')
  const [context, setContext] = useState('')

  // Participant picker
  const [participants, setParticipants] = useState<
    { id: string; firstName: string; lastName: string | null; avatarUrl: string | null }[]
  >([])
  const [showParticipantSearch, setShowParticipantSearch] = useState(false)
  const [participantQuery, setParticipantQuery] = useState('')
  const { data: contactResults } = useContacts(
    participantQuery ? { q: participantQuery, limit: 5 } : {},
  )

  const filteredResults = contactResults?.items.filter(
    (c) => c.id !== contactId && !participants.some((p) => p.id === c.id),
  )

  function addParticipant(c: Contact) {
    setParticipants((prev) => [
      ...prev,
      { id: c.id, firstName: c.firstName, lastName: c.lastName, avatarUrl: c.avatarUrl },
    ])
    setParticipantQuery('')
    setShowParticipantSearch(false)
  }

  function removeParticipant(id: string) {
    setParticipants((prev) => prev.filter((p) => p.id !== id))
  }

  // Voice input: when transcription completes, parse and fill fields
  async function handleVoiceToggle() {
    if (voice.state === 'recording') {
      const text = await voice.stopAndTranscribe()
      if (text) {
        // Use transcribed text to fill the context field and update title if it was default
        setContext((prev) => (prev ? `${prev}\n${text}` : text))
      }
    } else {
      await voice.startRecording()
    }
  }

  // Close on escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  function handleSave() {
    const contactIds = [contactId, ...participants.map((p) => p.id)]
    createInteraction.mutate(
      {
        contact_ids: contactIds,
        type: template.type,
        title: title.trim() || template.name,
        quality: template.quality,
        location: location.trim() || null,
        meeting_context: context.trim() || null,
        occurred_at: new Date(occurredAt).toISOString(),
      },
      {
        onSuccess: () => {
          onSaved()
          onClose()
        },
      },
    )
  }

  const isRecording = voice.state === 'recording'
  const isTranscribing = voice.state === 'transcribing'

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-card flex max-h-[85vh] w-full max-w-md flex-col rounded-t-2xl border shadow-lg sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-3">
          <div className="flex items-center gap-2">
            {template.icon && <span className="text-lg">{template.icon}</span>}
            <h2 className="text-base font-semibold">{template.name}</h2>
          </div>
          <div className="flex items-center gap-2">
            {/* Voice input button */}
            <button
              type="button"
              onClick={handleVoiceToggle}
              disabled={isTranscribing}
              className={cn(
                'flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-all',
                isRecording
                  ? 'bg-coral text-coral-foreground animate-pulse'
                  : isTranscribing
                    ? 'bg-surface-container text-on-surface-variant opacity-50'
                    : 'bg-surface-container hover:bg-surface-container-high text-on-surface-variant',
              )}
            >
              {isTranscribing ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : isRecording ? (
                <MicOff className="size-3.5" />
              ) : (
                <Mic className="size-3.5" />
              )}
              {isRecording
                ? `${voice.elapsed}s`
                : isTranscribing
                  ? t('quickLogPopup.transcribing', 'Transcribing...')
                  : t('quickLogPopup.voice', 'Voice')}
            </button>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1">
              <X className="size-4" />
            </button>
          </div>
        </div>

        {voice.error && <p className="text-destructive -mt-1 mb-1 px-5 text-xs">{voice.error}</p>}

        {/* Form */}
        <div className="flex-1 space-y-3 overflow-y-auto px-5 pb-4">
          {/* Title */}
          <div>
            <label className="text-on-surface-variant mb-1 flex items-center gap-1.5 text-xs font-medium">
              <Pencil className="size-3" />
              {t('quickLogPopup.title', 'Title')}
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="border-outline-variant/30 bg-surface-container focus:ring-ring w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2"
            />
          </div>

          {/* Date */}
          <div>
            <label className="text-on-surface-variant mb-1 flex items-center gap-1.5 text-xs font-medium">
              <Calendar className="size-3" />
              {t('quickLogPopup.date', 'Date')}
            </label>
            <input
              type="datetime-local"
              value={occurredAt}
              onChange={(e) => setOccurredAt(e.target.value)}
              className="border-outline-variant/30 bg-surface-container focus:ring-ring w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2"
            />
          </div>

          {/* Place */}
          <div>
            <label className="text-on-surface-variant mb-1 flex items-center gap-1.5 text-xs font-medium">
              <MapPin className="size-3" />
              {t('quickLogPopup.place', 'Place')}
            </label>
            <PlaceAutocomplete
              value={location}
              onChange={setLocation}
              placeholder={t('quickLogPopup.placePlaceholder', 'Where did it happen?')}
              className="border-outline-variant/30 bg-surface-container focus:ring-ring w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2"
            />
          </div>

          {/* Context */}
          <div>
            <label className="text-on-surface-variant mb-1 flex items-center gap-1.5 text-xs font-medium">
              <FileText className="size-3" />
              {t('quickLogPopup.context', 'Context')}
            </label>
            <textarea
              rows={2}
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder={t('quickLogPopup.contextPlaceholder', 'What was it about?')}
              className="border-outline-variant/30 bg-surface-container focus:ring-ring w-full resize-none rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2"
            />
          </div>

          {/* Participants */}
          <div>
            <label className="text-on-surface-variant mb-1 flex items-center gap-1.5 text-xs font-medium">
              <UserPlus className="size-3" />
              {t('quickLogPopup.participants', 'Other participants')}
            </label>

            {/* Selected participants */}
            {participants.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1.5">
                {participants.map((p) => (
                  <div
                    key={p.id}
                    className="bg-surface-container-low flex items-center gap-1 rounded-full py-0.5 pr-1.5 pl-0.5"
                  >
                    {p.avatarUrl ? (
                      <img src={p.avatarUrl} alt="" className="size-5 rounded-full object-cover" />
                    ) : (
                      <div className="bg-primary/10 text-primary flex size-5 items-center justify-center rounded-full text-[9px] font-semibold">
                        {getInitials(p.firstName, p.lastName)}
                      </div>
                    )}
                    <span className="text-xs">{getContactName(p)}</span>
                    <button
                      type="button"
                      onClick={() => removeParticipant(p.id)}
                      className="text-muted-foreground hover:text-foreground ml-0.5"
                    >
                      <X className="size-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Search / add button */}
            {showParticipantSearch ? (
              <div className="relative">
                <Search className="text-muted-foreground absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2" />
                <input
                  type="text"
                  value={participantQuery}
                  onChange={(e) => setParticipantQuery(e.target.value)}
                  placeholder={t('quickLogPopup.searchParticipant', 'Search contacts...')}
                  className="border-outline-variant/30 bg-surface-container focus:ring-ring w-full rounded-lg border py-1.5 pr-8 pl-8 text-sm outline-none focus:ring-2"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => {
                    setShowParticipantSearch(false)
                    setParticipantQuery('')
                  }}
                  className="text-muted-foreground hover:text-foreground absolute top-1/2 right-2.5 -translate-y-1/2"
                >
                  <X className="size-3.5" />
                </button>
                {participantQuery && filteredResults && filteredResults.length > 0 && (
                  <div className="border-outline-variant/20 bg-surface-container-highest/90 absolute top-full right-0 left-0 z-10 mt-1 max-h-36 overflow-y-auto rounded-xl shadow-lg backdrop-blur-xl">
                    {filteredResults.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        className="hover:bg-primary/5 flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm"
                        onClick={() => addParticipant(c)}
                      >
                        {c.avatarUrl ? (
                          <img
                            src={c.avatarUrl}
                            alt=""
                            className="size-6 rounded-full object-cover"
                          />
                        ) : (
                          <div className="bg-primary/10 text-primary flex size-6 items-center justify-center rounded-full text-[10px] font-semibold">
                            {getInitials(c.firstName, c.lastName)}
                          </div>
                        )}
                        <span className="text-xs font-medium">{getContactName(c)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowParticipantSearch(true)}
                className="border-outline-variant/30 hover:bg-surface-container-high text-on-surface-variant flex items-center gap-1.5 rounded-lg border border-dashed px-2.5 py-1.5 text-xs transition-colors"
              >
                <UserPlus className="size-3" />
                {t('quickLogPopup.addParticipant', 'Add participant')}
              </button>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="border-outline-variant/20 flex gap-2 border-t px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="bg-surface-container hover:bg-surface-container-high flex-1 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors"
          >
            {t('quickLogPopup.skip', 'Skip')}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={createInteraction.isPending}
            className="bg-primary text-primary-foreground flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {createInteraction.isPending && <Loader2 className="size-3.5 animate-spin" />}
            {t('quickLogPopup.save', 'Save')}
          </button>
        </div>
      </div>
    </div>
  )
}
